import { IconName } from '@fortawesome/fontawesome-svg-core';
import { SidebarSectionHeader } from 'elements/minute/sidebar/sidebarSection/SidebarSection/SidebarSectionHeader';
import { motion, useAnimation } from 'framer-motion';
import { EBorders } from 'libs/styles/styling';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
    topBorder?: boolean;
    disableBottomBorder?: boolean;
    initialExpanded?: boolean;
    title: string;
    icon?: {
        iconName: IconName;
    };
    children?: React.ReactNode;
}

export const SidebarSection = (props: Props): JSX.Element => {
    const initialExpanded: boolean =
        localStorage.getItem('sidebarSectionExpanded-' + props.title) === 'true' ||
        props.initialExpanded;
    const [expanded, setExpanded] = useState(initialExpanded);
    const firstTimeNoAnimation = useRef(true);
    const controls = useAnimation();

    useEffect(() => {
        reset();
        firstTimeNoAnimation.current = initialExpanded;
    }, []);

    function reset() {
        controls.stop();
        controls.set({
            height: 0,
            opacity: 0.8,
            overflow: 'hidden'
        });
    }

    function onAnimationComplete() {
        controls.set({ overflow: 'visible' });
    }

    useEffect(() => {
        async function anim() {
            if (expanded) {
                if (firstTimeNoAnimation.current) {
                    controls.start(
                        {
                            height: 'auto',
                            opacity: 1
                        },
                        {
                            duration: 0
                        }
                    );
                    firstTimeNoAnimation.current = false;
                } else {
                    controls.start(
                        {
                            height: 'auto',
                            opacity: 1
                        },
                        {
                            duration: 0.15
                        }
                    );
                }
            } else {
                reset();
            }
        }
        anim();
    }, [expanded]);

    return (
        <S_SidebarSection className={props.className}>
            <S_SidebarSectionHeader
                icon={props.icon}
                title={props.title}
                expanded={expanded}
                onExpandClick={() => {
                    reset();
                    setExpanded((val) => !val);
                    localStorage.setItem(
                        'sidebarSectionExpanded-' + props.title,
                        JSON.stringify(!expanded)
                    );
                }}
                $topBorder={props.topBorder}
            />
            <motion.div animate={controls} onAnimationComplete={onAnimationComplete}>
                <S_Display $visible={expanded}>
                    <S_Children $disableBottomBorder={props.disableBottomBorder}>
                        {props.children}
                    </S_Children>
                </S_Display>
            </motion.div>
        </S_SidebarSection>
    );
};

const S_SidebarSection = styled.div``;

const S_SidebarSectionHeader = styled(SidebarSectionHeader)<{
    $topBorder?: boolean;
}>`
    border-top: ${(props) => (props.$topBorder ? '1px solid' + EBorders.DARK : 'none')};
`;

const S_Children = styled.div<{ $disableBottomBorder: boolean }>`
    border-bottom: ${(props) =>
        props.$disableBottomBorder ? 'none' : '1px solid' + EBorders.DARK};
`;

const S_Display = styled.div<{ $visible: boolean }>`
    display: ${(props) => (props.$visible ? 'block' : 'none')};
`;
